/**
 * EditableText — contentEditable single-line text input.
 *
 * Extracted from ListTreeEditor for reuse. Handles the contentEditable
 * invariants:
 *
 * - JSX renders no children (text injected imperatively via ref) so React
 *   reconciliation doesn't clobber unsaved typing on parent re-render.
 * - Initial textContent set in useLayoutEffect before paint.
 * - External `value` change syncs to DOM, but never while the element is
 *   focused (would clobber the user's caret).
 * - `Enter` (insertParagraph) blocked — single-line semantics. Callers
 *   handle Enter via `onKeyDown` (e.g. submit, navigate).
 * - `locked` denies typing with a toast but keeps focus/navigation working.
 *
 * Caller controls layout/typography via `className`. Wrap in a container
 * for sizing.
 */

import { cn } from '@/lib/utils';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { toast } from 'sonner';

export interface EditableTextProps {
  value: string;
  placeholder?: string;
  ariaLabel?: string;
  /** When true, focus/navigation works but typing is denied with a toast. */
  locked?: boolean;
  /** Message shown in toast when locked input is typed into. */
  lockedMessage?: string;
  shouldAutoFocus?: boolean;
  className?: string;
  onFocus?: () => void;
  onBlur?: (newValue: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLSpanElement>, currentValue: string) => void;
}

export function EditableText({
  value,
  placeholder,
  ariaLabel,
  locked,
  lockedMessage = 'This field is locked.',
  shouldAutoFocus,
  className,
  onFocus,
  onBlur,
  onKeyDown,
}: EditableTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [localValue, setLocalValue] = useState(value);

  // Set initial textContent before paint so placeholder/empty state is correct
  // on first render (the JSX renders no children to avoid React reconciling
  // the text node and clobbering unsaved typing on parent re-render).
  useLayoutEffect(() => {
    if (ref.current && ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalValue(value);
    // Sync DOM if it diverged (e.g. after parent rebuild),
    // but don't clobber while user is actively typing.
    // This is the SOLE path syncing `value` -> DOM; JSX has no text child.
    if (
      ref.current &&
      ref.current.textContent !== value &&
      document.activeElement !== ref.current
    ) {
      ref.current.textContent = value;
    }
  }, [value]);

  useEffect(() => {
    if (shouldAutoFocus && ref.current) {
      ref.current.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(ref.current);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [shouldAutoFocus]);

  const handleInput = useCallback(() => {
    if (ref.current) setLocalValue(ref.current.textContent || '');
  }, []);

  const handleBlur = useCallback(() => {
    onBlur?.(ref.current?.textContent || '');
  }, [onBlur]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      onKeyDown?.(e, ref.current?.textContent || '');
    },
    [onKeyDown],
  );

  const handleBeforeInput = useCallback(
    (e: React.FormEvent<HTMLSpanElement>) => {
      const inputEvent = e.nativeEvent as InputEvent;
      if (inputEvent.inputType === 'insertParagraph') {
        e.preventDefault();
      }
      if (locked) {
        e.preventDefault();
        toast.info(lockedMessage);
      }
    },
    [locked, lockedMessage],
  );

  const isEmpty = !localValue;

  return (
    <span
      ref={ref}
      role="textbox"
      aria-label={ariaLabel}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      onFocus={onFocus}
      onBlur={handleBlur}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onBeforeInput={handleBeforeInput}
      className={cn(
        'block min-h-[1.25rem] w-full text-sm leading-tight outline-none focus:ring-0',
        isEmpty &&
          placeholder &&
          'empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]',
        className,
      )}
      data-placeholder={placeholder}
    />
  );
}
